// Home at /o/:slug (reset PR2; DS artboard 1a, ui_kits/desktop/HomeScreen.jsx): a greeting,
// "Upcoming" from the calendar the Meetings page reads (FR-M9), and the 8 most recent
// recordings. The artboard's action-items card and Up-next hero need meeting notes (M4) and
// are not built yet; nothing here is sample data.
//
// Old library deep links (`/o/:slug?q=…` or `?filter=…`) still land on the library: see
// legacyLibraryRedirect.
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Badge, Card, Icon, type BadgeProps } from "@grackle/ui";
import { api } from "../api/client";
import type { GrackleDetail, GrackleOut, PagedResponse } from "../api/types";
import { calendarStatus, listUpcoming } from "../calendar/client";
import { platformLabel, type MeetingPlatform } from "../calendar/platform";
import type { CalendarEvent } from "../calendar/types";
import { AppShell } from "../components/AppShell";
import { PageLoader, EmptyState } from "../components/ui";
import { useSession } from "../session";
import { cardPageStyle } from "./cardColumns";
import GrackleCard from "./GrackleCard";
import { legacyLibraryRedirect, libraryPath } from "./routes";
import "./library.css";

const RECENT_LIMIT = 8;
const UPCOMING_LIMIT = 3;

const PLATFORM_TONE: Record<MeetingPlatform, BadgeProps["tone"]> = {
  meet: "meet",
  zoom: "zoom",
  teams: "teams",
  other: "neutral",
};

export function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function firstName(name: string, email: string): string {
  const first = name.trim().split(/\s+/)[0];
  return first || email.split("@")[0] || "";
}

/** "10:30" — DS calendar times are 24-hour and mono. */
function clock(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** "Today" / "Tomorrow", else the weekday with the date for a second line ("Mon", "Oct 26"). */
function dayLabel(d: Date, now: Date): { day: string; date?: string } {
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / 86_400_000);
  if (days === 0) return { day: "Today" };
  if (days === 1) return { day: "Tomorrow" };
  return {
    day: new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(d),
    date: new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(d),
  };
}

/** Upcoming meetings, or null when the section must be hidden: no calendar connected, or the
 * status / events call failed (FR-M9 is optional; Home never shows a calendar error). */
function useUpcoming(orgId: string | undefined): { events: CalendarEvent[] | null; loading: boolean } {
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const status = await calendarStatus(orgId);
        if (!status.google.connected && !status.microsoft.connected) return null;
        const res = await listUpcoming(orgId);
        const now = Date.now();
        return res.items
          .filter((e) => new Date(e.ends_at ?? e.starts_at).getTime() >= now)
          .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
          .slice(0, UPCOMING_LIMIT);
      } catch {
        return null;
      }
    })().then((result) => {
      if (cancelled) return;
      setEvents(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);
  return { events, loading };
}

export default function HomePage() {
  const { slug } = useParams();
  const location = useLocation();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const [recent, setRecent] = useState<GrackleOut[] | null>(null);
  const [recentError, setRecentError] = useState(false);
  const redirect = slug ? legacyLibraryRedirect(slug, location.search) : null;
  const upcoming = useUpcoming(redirect ? undefined : org?.id);

  useEffect(() => {
    if (!org || redirect) return;
    let cancelled = false;
    setRecent(null);
    setRecentError(false);
    const params = new URLSearchParams({ filter: "all", limit: String(RECENT_LIMIT) });
    api<PagedResponse<GrackleOut>>("GET", `/orgs/${org.id}/grackles?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setRecent(res.items);
      })
      .catch(() => {
        if (!cancelled) {
          setRecent([]);
          setRecentError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [org?.id, redirect]);

  // FR-ST18: a poster URL can expire on screen; refetch that one item (same as the library).
  const onThumbnailStale = useCallback((grackleId: string) => {
    api<GrackleDetail>("GET", `/grackles/${grackleId}`)
      .then((detail) => {
        setRecent((prev) => prev?.map((g) => (g.id === grackleId ? { ...g, thumbnail_url: detail.thumbnail_url } : g)) ?? prev);
      })
      .catch(() => {});
  }, []);

  const onDeleted = useCallback((grackleId: string) => {
    setRecent((prev) => prev?.filter((g) => g.id !== grackleId) ?? prev);
  }, []);

  if (redirect) return <Navigate to={redirect} replace />;
  if (sessionLoading) return <PageLoader />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  const canRecord = user.email_verified;
  const now = new Date();
  const todayCount = upcoming.events?.filter((e) => dayLabel(new Date(e.starts_at), now).day === "Today").length ?? 0;
  const name = firstName(user.name, user.email);

  return (
    <AppShell org={org} user={user} section="home" canRecord={canRecord} fullWidth>
      <div className="home lib-page" style={cardPageStyle(recent?.length ?? RECENT_LIMIT)}>
        <div>
          <h1 className="gk-h1">
            {greetingFor(now)}
            {name ? `, ${name}` : ""}
          </h1>
          {todayCount > 0 ? (
            <p className="gk-body" style={{ marginTop: 8 }}>
              {todayCount === 1 ? "One meeting" : <><span className="mono">{todayCount}</span> meetings</>} on your
              calendar today.
            </p>
          ) : null}
        </div>

        {upcoming.events ? (
          <section className="home-section" aria-labelledby="home-upcoming">
            <div className="home-section__head">
              <h2 className="gk-h4" id="home-upcoming">
                Upcoming
              </h2>
              <Link to={`/o/${org.slug}/meetings`}>Meetings</Link>
            </div>
            {upcoming.events.length === 0 ? (
              <p className="gk-sm">Nothing coming up on your calendar.</p>
            ) : (
              <div className="home-events">
                {upcoming.events.map((e) => {
                  const start = new Date(e.starts_at);
                  const when = dayLabel(start, now);
                  return (
                    <Card key={e.id} size="sm" className="home-event">
                      <div className="home-event__time">
                        {clock(start)}
                        <span className="home-event__day">{when.day}</span>
                        {when.date ? <span className="home-event__day">{when.date}</span> : null}
                      </div>
                      <div className="home-event__body">
                        <span className="home-event__title">{e.title || "Untitled meeting"}</span>
                        <span>
                          <Badge size="sm" tone={PLATFORM_TONE[e.platform] ?? "neutral"}>
                            {platformLabel(e.platform)}
                          </Badge>
                        </span>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        ) : null}

        <section className="home-section" aria-labelledby="home-recent">
          <div className="home-section__head">
            <h2 className="gk-h4" id="home-recent">
              Recent
            </h2>
            <Link to={libraryPath(org.slug, { filter: "all" })}>See all</Link>
          </div>
          {recent === null ? (
            <PageLoader label="Loading recent recordings…" />
          ) : recent.length === 0 ? (
            <EmptyState
              className="lib-empty"
              icon={recentError ? "warning-circle" : "video-camera"}
              title={recentError ? "Recent recordings didn't load" : "No recordings yet"}
              body={
                recentError
                  ? "Your library is still there. Open it to try again."
                  : "Record your screen and send the link — no meeting needed."
              }
              action={
                recentError ? (
                  <Link to={libraryPath(org.slug)} className="gk-btn gk-btn--secondary">
                    Open library
                  </Link>
                ) : canRecord ? (
                  // Record is the ink pill (DS), like the shell's Record a video in the top bar.
                  <Link to="/record" className="gk-btn gk-btn--dark">
                    <Icon name="video-camera" size={20} />
                    Record a video
                  </Link>
                ) : null
              }
            />
          ) : (
            <div className="lib-grid">
              {recent.map((g) => (
                <GrackleCard key={g.id} grackle={g} onThumbnailStale={onThumbnailStale} onDeleted={onDeleted} />
              ))}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}
